import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class RingSizerTriggerComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleClick = async (event) => {
    const triggerButton = event.target instanceof HTMLElement ? event.target.closest('button') : null;
    if (!(triggerButton instanceof HTMLButtonElement)) return;

    event.preventDefault();

    if (triggerButton.disabled) return;

    const variantId = Number(this.dataset.ringSizerVariantId || 0);
    if (!variantId) {
      document.dispatchEvent(new CartErrorEvent('ring-sizer-trigger', 'Unable to load the ring sizer product. Please try again.'));
      return;
    }

    triggerButton.classList.add('atc-loading');
    triggerButton.disabled = true;

    try {
      const cart = await this.#fetchCart();
      await this.#closeSourceDialog();

      if (Number(cart?.item_count) > 0) {
        await this.#addToCart(variantId);
        return;
      }

      this.#showDialog();
    } catch (error) {
      console.error(error);
      const message = error instanceof Error && error.message
        ? error.message
        : 'Unable to check your bag right now. Please try again.';
      document.dispatchEvent(new CartErrorEvent('ring-sizer-trigger', message));
    } finally {
      triggerButton.classList.remove('atc-loading');
      triggerButton.disabled = false;
    }
  };

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.root}cart.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal: this.#abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    return response.json();
  }

  async #addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    const sections = this.#getCartSections();
    if (sections) {
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname + window.location.search);
    }

    if (this.dataset.sourceProductTitle) {
      formData.append('properties[_Ring sizer requested from]', this.dataset.sourceProductTitle);
    }

    formData.append('properties[_Source product URL]', this.dataset.sourceProductUrl || window.location.href);

    const requestConfig = fetchConfig('javascript', { body: formData });
    const response = await fetch(`${Theme.routes.root}cart/add.js`, {
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        Accept: 'text/html',
      },
      signal: this.#abortController.signal,
    }).then((result) => result.json());

    if (response.status) {
      const message = response.message || 'Unable to add the ring sizer right now.';
      throw new Error(message);
    }

    document.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: 'ring-sizer-trigger-component',
        itemCount: 1,
        productId: this.dataset.productId,
        sections: response.sections,
      })
    );
  }

  #getCartSections() {
    const sectionIds = new Set();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionIds).join(',');
  }

  async #closeSourceDialog() {
    const dialogId = this.dataset.closeDialogId;
    if (!dialogId) return;

    const dialogComponent = document.getElementById(dialogId);
    if (typeof dialogComponent?.closeDialog === 'function') {
      await dialogComponent.closeDialog();
    }
  }

  #showDialog() {
    const dialogId = this.dataset.dialogId;
    if (!dialogId) return;

    const dialogComponent = document.getElementById(dialogId);
    dialogComponent?.showDialog?.();
  }
}

if (!customElements.get('ring-sizer-trigger-component')) {
  customElements.define('ring-sizer-trigger-component', RingSizerTriggerComponent);
}

class RingSizerComponent extends Component {
  refs = {};
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const form = this.#form;

    form?.addEventListener('submit', this.#handleSubmit, { signal });

    this.#fields.forEach(({ input }) => {
      input?.addEventListener('input', this.#handleFieldChange, { signal });
      input?.addEventListener('change', this.#handleFieldChange, { signal });
      input?.addEventListener('blur', this.#handleFieldBlur, { signal });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get #form() {
    const form = this.querySelector('form');
    return form instanceof HTMLFormElement ? form : null;
  }

  get #fields() {
    return [
      {
        input: this.refs.nameInput instanceof HTMLInputElement ? this.refs.nameInput : null,
        error: this.refs.nameError instanceof HTMLElement ? this.refs.nameError : null,
      },
      {
        input: this.refs.emailInput instanceof HTMLInputElement ? this.refs.emailInput : null,
        error: this.refs.emailError instanceof HTMLElement ? this.refs.emailError : null,
      },
      {
        input: this.refs.address1Input instanceof HTMLInputElement ? this.refs.address1Input : null,
        error: this.refs.address1Error instanceof HTMLElement ? this.refs.address1Error : null,
      },
      {
        input: this.refs.cityInput instanceof HTMLInputElement ? this.refs.cityInput : null,
        error: this.refs.cityError instanceof HTMLElement ? this.refs.cityError : null,
      },
      {
        input: this.refs.postcodeInput instanceof HTMLInputElement ? this.refs.postcodeInput : null,
        error: this.refs.postcodeError instanceof HTMLElement ? this.refs.postcodeError : null,
      },
      {
        input: this.refs.countryInput instanceof HTMLSelectElement ? this.refs.countryInput : null,
        error: this.refs.countryError instanceof HTMLElement ? this.refs.countryError : null,
      },
    ];
  }

  #handleSubmit = async (event) => {
    event.preventDefault();

    if (!this.#validateForm()) return;

    const submitButton = this.refs.submitButton instanceof HTMLButtonElement ? this.refs.submitButton : null;
    const variantId = Number(this.dataset.ringSizerVariantId || 0);

    if (!variantId) {
      this.#setFormError('Unable to load the ring sizer product. Please try again.');
      return;
    }

    submitButton?.classList.add('atc-loading');
    if (submitButton) submitButton.disabled = true;
    this.#clearFormError();

    try {
      const values = this.#getValues();
      window.location.assign(this.#buildCartPermalink(variantId, values).toString());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to submit your order right now.';
      this.#setFormError(message);
    } finally {
      submitButton?.classList.remove('atc-loading');
      if (submitButton) submitButton.disabled = false;
    }
  };

  #handleFieldChange = (event) => {
    const field = event.currentTarget;
    if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLSelectElement)) return;

    const matchingField = this.#fields.find(({ input }) => input === field);
    if (!matchingField) return;

    this.#updateFieldState(matchingField, true);
  };

  #handleFieldBlur = (event) => {
    const field = event.currentTarget;
    if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLSelectElement)) return;

    const matchingField = this.#fields.find(({ input }) => input === field);
    if (!matchingField) return;

    this.#updateFieldState(matchingField, false);
  };

  #validateForm() {
    let isValid = true;

    this.#fields.forEach((field) => {
      const fieldIsValid = this.#updateFieldState(field, false);
      if (!fieldIsValid) isValid = false;
    });

    return isValid;
  }

  #updateFieldState(field, hideIfValid) {
    const { input, error } = field;
    if (!input) return true;

    const isValid = input.checkValidity();

    input.classList.toggle('ring-sizer-field-input--invalid', !isValid);
    input.setAttribute('aria-invalid', String(!isValid));

    if (error) {
      if (!error.dataset.defaultMessage) {
        error.dataset.defaultMessage = error.textContent || '';
      }

      error.classList.toggle('hidden', hideIfValid ? true : isValid);

      if (!isValid) {
        if (input instanceof HTMLInputElement && input.type === 'email' && input.validity.typeMismatch) {
          error.textContent = error.dataset.emailMessage || error.dataset.defaultMessage;
        } else {
          error.textContent = error.dataset.defaultMessage;
        }
      }
    }

    return isValid;
  }

  #getValues() {
    const name = this.refs.nameInput instanceof HTMLInputElement ? this.refs.nameInput.value.trim() : '';
    const email = this.refs.emailInput instanceof HTMLInputElement ? this.refs.emailInput.value.trim() : '';
    const address1 = this.refs.address1Input instanceof HTMLInputElement ? this.refs.address1Input.value.trim() : '';
    const address2 = this.refs.address2Input instanceof HTMLInputElement ? this.refs.address2Input.value.trim() : '';
    const city = this.refs.cityInput instanceof HTMLInputElement ? this.refs.cityInput.value.trim() : '';
    const state = this.refs.stateInput instanceof HTMLInputElement ? this.refs.stateInput.value.trim() : '';
    const postcode = this.refs.postcodeInput instanceof HTMLInputElement ? this.refs.postcodeInput.value.trim() : '';
    const country = this.refs.countryInput instanceof HTMLSelectElement ? this.refs.countryInput.value.trim() : '';

    return {
      name,
      email,
      address1,
      address2,
      city,
      state,
      postcode,
      country,
    };
  }

  #buildCartPermalink(variantId, values) {
    const checkoutUrl = new URL(`${Theme.routes.root}cart/${variantId}:1`, window.location.origin);
    const { firstName, lastName } = this.#splitName(values.name);
    const properties = {
      Name: values.name,
      Email: values.email,
      'Street Address': values.address1,
      City: values.city,
      Postcode: values.postcode,
      Country: values.country,
      '_Source product URL': this.dataset.sourceProductUrl || window.location.href,
    };

    if (values.address2) {
      properties['Address Line 2'] = values.address2;
    }

    if (values.state) {
      properties.State = values.state;
    }

    if (this.dataset.sourceProductTitle) {
      properties['_Ring sizer requested from'] = this.dataset.sourceProductTitle;
    }

    checkoutUrl.searchParams.set('checkout[email]', values.email);
    checkoutUrl.searchParams.set('checkout[shipping_address][first_name]', firstName);
    checkoutUrl.searchParams.set('checkout[shipping_address][last_name]', lastName);
    checkoutUrl.searchParams.set('checkout[shipping_address][address1]', values.address1);

    if (values.address2) {
      checkoutUrl.searchParams.set('checkout[shipping_address][address2]', values.address2);
    }

    checkoutUrl.searchParams.set('checkout[shipping_address][city]', values.city);
    checkoutUrl.searchParams.set('checkout[shipping_address][zip]', values.postcode);
    checkoutUrl.searchParams.set('checkout[shipping_address][country]', values.country);

    if (values.state) {
      checkoutUrl.searchParams.set('checkout[shipping_address][province]', values.state);
    }

    checkoutUrl.searchParams.set('properties', this.#encodeProperties(properties));

    return checkoutUrl;
  }

  #encodeProperties(properties) {
    const json = JSON.stringify(properties);
    const bytes = new TextEncoder().encode(json);
    let binary = '';

    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/u, '');
  }

  #splitName(name) {
    const parts = name.split(/\s+/).filter(Boolean);
    const firstName = parts.shift() || name;
    const lastName = parts.join(' ');

    return {
      firstName,
      lastName,
    };
  }

  #setFormError(message) {
    if (!(this.refs.formError instanceof HTMLElement)) return;

    this.refs.formError.textContent = message;
    this.refs.formError.classList.remove('hidden');
  }

  #clearFormError() {
    if (!(this.refs.formError instanceof HTMLElement)) return;

    this.refs.formError.textContent = '';
    this.refs.formError.classList.add('hidden');
  }
}

if (!customElements.get('ring-sizer-component')) {
  customElements.define('ring-sizer-component', RingSizerComponent);
}
